import React, { useCallback } from 'react';
import classNames from 'classnames';
import classes from './ElementOverlay.module.scss';
import { WithChildren } from '../../../models/children-props';

export enum ElementOverlaySize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE'
}
export enum ElementOverlayPositions {
  TopLeft = 'TopLeft',
  TopCenter = 'TopCenter',
  TopRight = 'TopRight',
  BottomLeft = 'BottomLeft',
  BottomCenter = 'BottomCenter',
  BottomRight = 'BottomRight',
  MiddleCenter = 'MiddleCenter'
}

export enum ElementOverlayCursor {
  magnify = 'Magnify',
  pointer = 'Pointer',
  move = 'Move',
  default = 'Default'
}

export type ElementOverlayViewProps = WithChildren & {
  defaultAction?: () => void;
  topLeft?: JSX.Element;
  topCenter?: JSX.Element;
  topRight?: JSX.Element;
  bottomLeft?: JSX.Element;
  bottomCenter?: JSX.Element;
  bottomRight?: JSX.Element;
  middleCenter?: JSX.Element;
  stickyPositions?: ElementOverlayPositions[];
  size: ElementOverlaySize;
  cursor?: ElementOverlayCursor;
};

export const ElementOverlayView = ({
  children,
  defaultAction,
  topLeft,
  topCenter,
  topRight,
  bottomLeft,
  bottomCenter,
  bottomRight,
  middleCenter,
  stickyPositions,
  size,
  cursor
}: ElementOverlayViewProps): JSX.Element => {
  const elementOverlayClasses = classNames(
    classes.ElementOverlay,
    defaultAction && classes[`ElementOverlayWith${cursor || ElementOverlayCursor.pointer}Cursor`]
  );
  const rowClasses = (position: string) => classNames(classes.OverlayRow, classes[position], classes[size]);

  const cellClasses = useCallback(
    (position: ElementOverlayPositions, isEmpty: boolean) =>
      classNames(classes.OverlayCell, 'cell', position.includes('Center') ? 'auto' : 'shrink', {
        [classes.AlwaysVisible]: stickyPositions?.includes(position),
        [classes.PositionInactive]: isEmpty
      }),
    [stickyPositions]
  );

  return (
    <div className={elementOverlayClasses}>
      <div onClick={defaultAction}>{children}</div>

      {/* TOP ROW */}
      <div className={classNames('grid-x', rowClasses('Top'))}>
        <div className={cellClasses(ElementOverlayPositions.TopLeft, !topLeft)}>{topLeft}</div>
        <div className={cellClasses(ElementOverlayPositions.TopCenter, !topCenter)}>{topCenter}</div>
        <div className={cellClasses(ElementOverlayPositions.TopRight, !topRight)}>{topRight}</div>
      </div>

      {/* MIDDLE ROW */}
      <div className={classNames('grid-x', rowClasses('Middle'))} onClick={defaultAction}>
        <div className={cellClasses(ElementOverlayPositions.MiddleCenter, !middleCenter)}>{middleCenter}</div>
      </div>

      {/* BOTTOM ROW */}
      <div className={classNames('grid-x', rowClasses('Bottom'))}>
        <div className={cellClasses(ElementOverlayPositions.BottomLeft, !bottomLeft)}>{bottomLeft}</div>
        {/* Adding onclick event to temp-correct issue https://app.asana.com/0/0/1209265074896447/f */}
        <div className={cellClasses(ElementOverlayPositions.BottomCenter, !bottomCenter)} onClick={defaultAction}>
          {bottomCenter}
        </div>
        <div className={cellClasses(ElementOverlayPositions.BottomRight, !bottomRight)}>{bottomRight}</div>
      </div>
    </div>
  );
};
